import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import useShop from '@app/react-common/hooks/useShop';
import {
  DateRangePreset,
  getDecadeStart,
  getEarliestDate,
  getPresetStart,
  getYearPage,
  getYearPageTitle,
  LAST_30_DAYS_PRESET,
  YEARS_PER_PAGE,
} from '@app/storybook/components/datepicker/date-range-presets';

export interface DateRangeApplyEvent {
  start: string;
  end: string;
  rangePreset: string;
}

export interface CalendarDay {
  key: string;
  date: moment.Moment;
  inMonth: boolean;
  available: boolean;
  isStart: boolean;
  isEnd: boolean;
  isSpanStart: boolean;
  isSpanEnd: boolean;
  inRange: boolean;
}

interface SelectedRange {
  start: moment.Moment | null;
  end: moment.Moment | null;
}

interface UseDateRangePickerProps {
  minDate?: string | Date;
  /** range emitted on load and restored by Reset */
  defaultPreset?: DateRangePreset;
  onApply?: (dateRange: DateRangeApplyEvent) => void;
}

export interface DateRangePickerState {
  containerRef: React.RefObject<HTMLDivElement>;
  isOpen: boolean;
  isYearMode: boolean;
  navDate: moment.Moment;
  days: CalendarDay[];
  yearPage: number[];
  yearPageTitle: string;
  activePreset: DateRangePreset | null;
  /** the pending selection, shown in the footer */
  displayValue: string;
  /** what the parent last received, shown in the input */
  appliedValue: string;
  isYearAvailable: (year: number) => boolean;
  toggleCalendar: () => void;
  selectPreset: (preset: DateRangePreset) => void;
  selectDay: (day: CalendarDay) => void;
  changeNav: (num: number) => void;
  openYearPicker: () => void;
  selectYear: (year: number) => void;
  applyDateRange: () => void;
  closeCalendar: () => void;
  resetToDefault: () => void;
}

const API_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'MMM DD, YYYY';

function buildMonthDays(
  navDate: moment.Moment,
  earliestDate: moment.Moment,
  latestDate: moment.Moment,
  range: SelectedRange,
): CalendarDay[] {
  const lastDay = navDate.clone().endOf('month').endOf('week');
  const isSpan = !!range.start && !!range.end && !range.start.isSame(range.end, 'day');
  const days: CalendarDay[] = [];
  for (const date = navDate.clone().startOf('month').startOf('week'); date.isSameOrBefore(lastDay, 'day'); ) {
    const day = date.clone();
    const inMonth = day.isSame(navDate, 'month');
    const isStart = inMonth && !!range.start?.isSame(day, 'day');
    const isEnd = inMonth && !!range.end?.isSame(day, 'day');
    days.push({
      key: day.format(API_DATE_FORMAT),
      date: day,
      inMonth,
      available: inMonth && day.isBetween(earliestDate, latestDate, 'day', '[]'),
      isStart,
      isEnd,
      isSpanStart: isStart && isSpan,
      isSpanEnd: isEnd && isSpan,
      inRange: inMonth && !!range.start && !!range.end && day.isBetween(range.start, range.end, 'day', '()'),
    });
    date.add(1, 'day');
  }
  return days;
}

function describeRange(range: SelectedRange, preset: DateRangePreset | null): string {
  if (preset) return preset.text;
  if (!range.start) return '';
  return `${range.start.format(DISPLAY_DATE_FORMAT)} - ${(range.end ?? range.start).format(DISPLAY_DATE_FORMAT)}`;
}

export function useDateRangePicker({
  minDate,
  defaultPreset = LAST_30_DAYS_PRESET,
  onApply,
}: UseDateRangePickerProps): DateRangePickerState {
  const shop = useShop();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasEmittedDefault = useRef(false);
  const earliestDate = useMemo(
    () => moment.max(getEarliestDate(shop?.created), moment.utc(minDate ?? 0).startOf('day')),
    [shop, minDate],
  );
  const [isOpen, setIsOpen] = useState(false);
  // recomputed on open, a page left open past midnight would otherwise keep today disabled
  const latestDate = useMemo(() => moment.utc().endOf('day'), [isOpen]);
  const [isYearMode, setIsYearMode] = useState(false);
  const [navDate, setNavDate] = useState(() => moment.utc());
  const [yearPageStart, setYearPageStart] = useState(0);
  const [range, setRange] = useState<SelectedRange>({ start: null, end: null });
  const [activePreset, setActivePreset] = useState<DateRangePreset | null>(defaultPreset);
  const [applied, setApplied] = useState<{ range: SelectedRange; preset: DateRangePreset | null }>({
    range: { start: null, end: null },
    preset: defaultPreset,
  });

  const days = useMemo(
    () => buildMonthDays(navDate, earliestDate, latestDate, range),
    [navDate, earliestDate, latestDate, range],
  );
  const yearPage = useMemo(() => getYearPage(yearPageStart), [yearPageStart]);

  const emitApply = useCallback(
    (start: moment.Moment, end: moment.Moment, preset: DateRangePreset | null) => {
      setApplied({ range: { start, end }, preset });
      onApply?.({
        start: start.format(API_DATE_FORMAT),
        end: end.format(API_DATE_FORMAT),
        rangePreset: preset?.text ?? '',
      });
    },
    [onApply],
  );

  // the parent has no dates until this fires, it is the default range load
  useEffect(() => {
    if (!shop || hasEmittedDefault.current) return;
    hasEmittedDefault.current = true;
    emitApply(getPresetStart(defaultPreset, earliestDate), moment.utc(), defaultPreset);
  }, [shop, earliestDate, defaultPreset, emitApply]);

  // drops whatever was picked since the last apply
  const closeCalendar = useCallback(() => {
    setRange(applied.range);
    setActivePreset(applied.preset);
    setIsOpen(false);
  }, [applied]);

  useEffect(() => {
    if (!isOpen) return;
    // the year/month swap detaches the clicked node, so contains() would close the picker
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!event.composedPath().includes(containerRef.current)) closeCalendar();
    };
    document.addEventListener('click', closeOnOutsideClick);
    return () => document.removeEventListener('click', closeOnOutsideClick);
  }, [isOpen, closeCalendar]);

  const selectPreset = useCallback(
    (preset: DateRangePreset) => {
      const end = moment.utc();
      setRange({ start: getPresetStart(preset, earliestDate), end });
      setActivePreset(preset);
      setNavDate(end.clone());
    },
    [earliestDate],
  );

  const toggleCalendar = useCallback(() => {
    if (isOpen) return closeCalendar();
    setIsYearMode(false);
    if (activePreset) selectPreset(activePreset);
    else setNavDate((applied.range.end ?? moment.utc()).clone());
    setIsOpen(true);
  }, [isOpen, activePreset, applied, selectPreset, closeCalendar]);

  const selectDay = useCallback(
    (day: CalendarDay) => {
      if (!day.available) return;
      const isPickingStart = !range.start || range.end;
      setRange(
        isPickingStart
          ? { start: day.date, end: null }
          : { start: moment.min(range.start, day.date), end: moment.max(range.start, day.date) },
      );
      setActivePreset(null);
    },
    [range],
  );

  const isYearAvailable = useCallback(
    (year: number) => year >= earliestDate.year() && year <= latestDate.year(),
    [earliestDate, latestDate],
  );

  const changeNav = useCallback(
    (num: number) => {
      if (isYearMode) {
        const nextPageStart = yearPageStart + num * YEARS_PER_PAGE;
        if (getYearPage(nextPageStart).some(isYearAvailable)) setYearPageStart(nextPageStart);
        return;
      }
      const target = navDate.clone().add(num, 'month');
      if (target.isBetween(earliestDate, latestDate, 'month', '[]')) setNavDate(target);
    },
    [isYearMode, yearPageStart, isYearAvailable, navDate, earliestDate, latestDate],
  );

  const openYearPicker = useCallback(() => {
    setYearPageStart(getDecadeStart(navDate.year()));
    setIsYearMode(true);
  }, [navDate]);

  const selectYear = useCallback(
    (year: number) => {
      if (!isYearAvailable(year)) return;
      setNavDate(moment.min(moment.max(navDate.clone().year(year), earliestDate), latestDate));
      setIsYearMode(false);
    },
    [isYearAvailable, navDate, earliestDate, latestDate],
  );

  const applyDateRange = useCallback(() => {
    if (!range.start) return;
    emitApply(range.start, range.end ?? range.start, activePreset);
    setIsOpen(false);
  }, [range, activePreset, emitApply]);

  const resetToDefault = useCallback(() => selectPreset(defaultPreset), [defaultPreset, selectPreset]);

  const displayValue = describeRange(range, activePreset);
  const appliedValue = describeRange(applied.range, applied.preset);

  return {
    containerRef,
    isOpen,
    isYearMode,
    navDate,
    days,
    yearPage,
    yearPageTitle: getYearPageTitle(yearPageStart),
    activePreset,
    displayValue,
    appliedValue,
    isYearAvailable,
    toggleCalendar,
    selectPreset,
    selectDay,
    changeNav,
    openYearPicker,
    selectYear,
    applyDateRange,
    closeCalendar,
    resetToDefault,
  };
}
